'use client';

import { useId, useMemo, useState } from 'react';
import { ChevronDown } from 'lucide-react';
import ExpandArrowIcon from '../icons/expand-arrow-icon';
import { Button } from '../ui/button';
import { Label } from '../ui/label';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import { RadioGroup, RadioGroupItem } from '../ui/radio-group';
import { cn } from '../cn';
import {
  SELECT_CHIP_LABEL_CLASSNAME,
  SELECT_CHIP_TRIGGER_CLASSNAME,
} from './select-trigger-styles';

export type RadioSelectOption = {
  disabled?: boolean;
  label: string;
  value: string;
};

export type RadioSelectProps = {
  className?: string;
  contentClassName?: string;
  disabled?: boolean;
  emptyLabel?: string;
  emptyValue?: string;
  isChip?: boolean;
  onValueChange: (value: string) => void;
  options: RadioSelectOption[];
  placeholder?: string;
  showCount?: boolean;
  variant?: 'default' | 'filter';
  value: string;
};

export function RadioSelect({
  className,
  contentClassName,
  disabled = false,
  emptyLabel = 'Tất cả',
  emptyValue = 'all',
  isChip = false,
  onValueChange,
  options,
  placeholder = 'Chọn',
  showCount = true,
  variant = 'default',
  value,
}: RadioSelectProps) {
  const inputId = useId();
  const [isOpen, setIsOpen] = useState(false);
  const optionsByValue = useMemo(
    () => new Map(options.map(option => [option.value, option])),
    [options]
  );
  const selectedOption = optionsByValue.get(value);
  const isFilterVariant = variant === 'filter';
  const triggerLabel =
    value === emptyValue || !selectedOption
      ? placeholder
      : `${selectedOption.label}${showCount ? ' (1)' : ''}`;

  const selectValue = (nextValue: string) => {
    onValueChange(nextValue);
    setIsOpen(false);
  };

  return (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          role="combobox"
          aria-expanded={isOpen}
          aria-haspopup="dialog"
          aria-label={`${placeholder}: ${triggerLabel}`}
          data-active={value !== emptyValue}
          disabled={disabled}
          className={cn(
            'border-alias-outline hover:border-primary bg-surface/95 text-gray-60 data-[active=true]:border-primary data-[active=true]:text-primary data-[state=open]:border-primary data-[state=open]:text-primary h-9 w-fit max-w-full min-w-0 justify-start gap-1 rounded-full border px-3 py-2 text-sm font-medium shadow-none hover:bg-transparent',
            isChip && SELECT_CHIP_TRIGGER_CLASSNAME,
            className
          )}
        >
          <span
            className={cn('truncate', isChip && SELECT_CHIP_LABEL_CLASSNAME)}
          >
            {triggerLabel}
          </span>
          {isChip ? (
            <span
              className="inline-flex size-4 shrink-0 items-center justify-center"
              aria-hidden
            >
              <ExpandArrowIcon
                className="size-[11px]"
                size={11}
                variant="filled"
              />
            </span>
          ) : (
            <ChevronDown className="size-4 shrink-0" aria-hidden />
          )}
        </Button>
      </PopoverTrigger>

      <PopoverContent
        align="start"
        className={cn(
          'border-outline-variant bg-surface w-[min(var(--radix-popover-trigger-width),calc(100vw-2rem))] min-w-64 overflow-hidden rounded-xl border p-1 shadow-md',
          isFilterVariant &&
          'border-search-suggestions-outline bg-surface-pure shadow-search-suggestions max-h-[min(26.5rem,var(--radix-popover-content-available-height))] rounded-[18px] px-2 py-3',
          contentClassName
        )}
      >
        <RadioGroup
          value={value}
          onValueChange={selectValue}
          aria-label={placeholder}
          className={cn(
            'overflow-y-auto',
            isFilterVariant
              ? 'hide-scrollbar max-h-[26rem] gap-0'
              : 'max-h-72 gap-1'
          )}
        >
          <Label
            htmlFor={`${inputId}-all`}
            className={cn(
              'hover:bg-primary-variant flex cursor-pointer items-center px-3 py-2 font-normal',
              isFilterVariant
                ? 'min-h-11 gap-2.5 rounded-xl px-4 text-base leading-5'
                : 'min-h-10 gap-3 rounded-lg text-sm',
              !isFilterVariant &&
              value === emptyValue &&
              'bg-primary-variant text-on-primary-variant'
            )}
          >
            <RadioGroupItem
              id={`${inputId}-all`}
              value={emptyValue}
              className={cn(isFilterVariant && 'size-6 [&_svg]:size-4')}
            />
            <span className="min-w-0 flex-1 truncate">{emptyLabel}</span>
          </Label>

          {options.map(option => {
            const optionId = `${inputId}-${encodeURIComponent(option.value)}`;
            const isSelected = value === option.value;

            return (
              <Label
                key={option.value}
                htmlFor={optionId}
                className={cn(
                  'hover:bg-primary-variant flex cursor-pointer items-center px-3 py-2 font-normal',
                  isFilterVariant
                    ? 'min-h-11 gap-2.5 rounded-xl px-4 text-base leading-5'
                    : 'min-h-10 gap-3 rounded-lg text-sm',
                  !isFilterVariant &&
                  isSelected &&
                  'bg-primary-variant text-on-primary-variant',
                  option.disabled &&
                  'text-content-disabled pointer-events-none cursor-not-allowed'
                )}
              >
                <RadioGroupItem
                  id={optionId}
                  value={option.value}
                  disabled={option.disabled}
                  aria-label={option.label}
                  className={cn(isFilterVariant && 'size-6 [&_svg]:size-4')}
                />
                <span className="min-w-0 flex-1 truncate">{option.label}</span>
              </Label>
            );
          })}
        </RadioGroup>
      </PopoverContent>
    </Popover>
  );
}
